import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { format } from "date-fns";
import PageHeader from "../../components/PageHeader";
import MessagesGrid from "../../components/notificationCenter/MessagesGrid";
import UsageTiles from "../../components/notificationCenter/UsageTiles";
import PriceList from "../../components/notificationCenter/PriceList";
import { CHANNEL_NAMES, contactUsLink } from "../../components/notificationCenter/channelText";
import { useUser } from "../../contexts/UserContext";
import {
  CenterChannel,
  ChannelInfo,
  ChannelPricing,
  NotificationCenterOverview,
  NotificationEvent,
  NotificationUsage,
  getNotificationCenter,
  getNotificationUsage,
  requestNotificationChannel,
  formatMoney,
  formatUnitPrice,
  updateNotificationCells,
  usageRange,
} from "../../services/notificationCenterService";

const errorMessage = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || fallback;
};

const CHANNEL_BLURB: Record<CenterChannel, string> = {
  PUSH: "Sent to the app on their phone, and to your browser when you allow it.",
  EMAIL: "Sent to the email address saved on their profile.",
  SMS: "A text message to their phone number.",
  WHATSAPP: "A WhatsApp message to their phone number.",
};

const SectionTitle: React.FC<{ title: string; hint?: string; actions?: React.ReactNode }> = ({
  title,
  hint,
  actions,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.15rem" }}>
        {title}
      </Typography>
      {hint && <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>{hint}</Typography>}
    </Box>
    {actions}
  </Box>
);

const ChannelCard: React.FC<{
  info: ChannelInfo;
  pricing: ChannelPricing | undefined;
  currency: string;
  defaultSender: string;
  contactWhatsapp: string | null;
  isOwner: boolean;
  requesting: boolean;
  onRequest: (channel: CenterChannel) => void;
}> = ({ info, pricing, currency, defaultSender, contactWhatsapp, isOwner, requesting, onRequest }) => {
  const name = CHANNEL_NAMES[info.channel];
  const status = !info.paid
    ? { label: "Included", color: "success" as const }
    : info.enabled
      ? { label: "On", color: "success" as const }
      : info.requestedAt
        ? { label: "Requested", color: "warning" as const }
        : { label: "Off", color: "default" as const };
  const contact = contactUsLink(contactWhatsapp, `Hello, I'd like to turn on ${name} messages for my account.`);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        p: 2.25,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, flex: 1 }}>{name}</Typography>
        <Chip size="small" label={status.label} color={status.color} variant="outlined" />
      </Box>
      <Typography sx={{ fontSize: "0.88rem", color: "text.secondary" }}>{CHANNEL_BLURB[info.channel]}</Typography>
      {info.paid && (
        <>
          <Typography sx={{ fontSize: "0.85rem" }}>
            {pricing?.current?.price != null
              ? `${formatUnitPrice(pricing.current.price, currency)} per delivered message.`
              : "Extra charges apply. We'll share the pricing soon."}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
            Sent as <strong>{info.senderId || defaultSender}</strong>
          </Typography>
          {!info.enabled && (
            <Box sx={{ mt: "auto", pt: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {info.requestedAt ? (
                <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                  Requested on {format(new Date(info.requestedAt), "d MMM yyyy")}. We'll be in touch.
                </Typography>
              ) : (
                isOwner && (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={requesting}
                    onClick={() => onRequest(info.channel)}
                    startIcon={requesting ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={{ textTransform: "none" }}
                  >
                    Request {name}
                  </Button>
                )
              )}
              {contact && (
                <Link href={contact} target="_blank" rel="noopener noreferrer" sx={{ fontSize: "0.85rem" }}>
                  Contact us
                </Link>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

/** The period's charges, and the messages that are on us. */
const ChargesSummary: React.FC<{ usage: NotificationUsage }> = ({ usage }) => {
  const freeSent = usage.byEvent
    .filter((row) => row.free)
    .reduce((sum, row) => sum + row.channels.SMS.sent + row.channels.WHATSAPP.sent, 0);
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        p: 2,
        display: "flex",
        gap: 2,
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Box sx={{ minWidth: 150 }}>
        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>Charges</Typography>
        <Typography sx={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}>
          {formatMoney(usage.amount, usage.currency)}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
          SMS {formatMoney(usage.totals.SMS.amount, usage.currency)} · WhatsApp{" "}
          {formatMoney(usage.totals.WHATSAPP.amount, usage.currency)}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontSize: "0.86rem" }}>
          You're only charged for messages that were delivered. Failed messages are free.
        </Typography>
        <Typography sx={{ fontSize: "0.86rem", color: "text.secondary" }}>
          {freeSent > 0
            ? `${freeSent.toLocaleString()} login ${freeSent === 1 ? "code" : "codes"} sent by WhatsApp or SMS, free of charge.`
            : "Login and password reset codes are always free of charge."}
        </Typography>
      </Box>
    </Paper>
  );
};

/**
 * Which messages go out, on which channel, and how many were sent. Owners
 * switch messages on and off; SMS and WhatsApp are turned on by us on request
 * because each message costs money.
 */
const NotificationCenter: React.FC = () => {
  const { user } = useUser();
  const isOwner = user?.role !== "EMPLOYEE";
  const [overview, setOverview] = useState<NotificationCenterOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [requesting, setRequesting] = useState<CenterChannel | null>(null);
  const [period, setPeriod] = useState<"this" | "last">("this");
  const [usage, setUsage] = useState<NotificationUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);

  useEffect(() => {
    getNotificationCenter()
      .then(setOverview)
      .catch((err) => setLoadError(errorMessage(err, "Could not load your notification settings.")));
  }, []);

  useEffect(() => {
    setUsageLoading(true);
    getNotificationUsage(usageRange(period))
      .then(setUsage)
      .catch(() => setUsage(null))
      .finally(() => setUsageLoading(false));
  }, [period]);

  const toggle = useCallback(
    async (event: NotificationEvent, channel: CenterChannel, enabled: boolean) => {
      const key = `${event.key}:${channel}`;
      const previous = overview;
      // Flip the switch straight away; roll back if the save fails.
      setOverview((current) =>
        current
          ? {
              ...current,
              events: current.events.map((e) =>
                e.key === event.key && e.cells[channel]
                  ? { ...e, cells: { ...e.cells, [channel]: { ...e.cells[channel]!, enabled } } }
                  : e
              ),
            }
          : current
      );
      setSaving((s) => new Set(s).add(key));
      try {
        setOverview(await updateNotificationCells([{ event: event.key, channel, enabled }]));
      } catch (err) {
        setOverview(previous);
        toast.error(errorMessage(err, "Could not save. Please try again."), { position: "top-right" });
      } finally {
        setSaving((s) => {
          const next = new Set(s);
          next.delete(key);
          return next;
        });
      }
    },
    [overview]
  );

  const request = async (channel: CenterChannel) => {
    setRequesting(channel);
    try {
      setOverview(await requestNotificationChannel(channel));
      toast.success(`Request sent. We'll contact you to turn on ${CHANNEL_NAMES[channel]}.`, {
        position: "top-right",
      });
    } catch (err) {
      toast.error(errorMessage(err, "Could not send the request."), { position: "top-right" });
    } finally {
      setRequesting(null);
    }
  };

  const senderContact = contactUsLink(
    overview?.contactWhatsapp,
    "Hello, I'd like to send messages with my own sender name."
  );

  return (
    <Box sx={{ width: "100%", maxWidth: 1000, mx: "auto", mt: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Notification center"
        subtitle="Choose which messages go out and on which channel, and see how many were sent and what they cost."
      />

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Box component="section" sx={{ mb: 4 }}>
        <SectionTitle
          title="Messages sent"
          actions={
            <ToggleButtonGroup
              size="small"
              exclusive
              value={period}
              onChange={(_, value) => value && setPeriod(value)}
            >
              <ToggleButton value="this" sx={{ textTransform: "none", px: 1.5 }}>
                This month
              </ToggleButton>
              <ToggleButton value="last" sx={{ textTransform: "none", px: 1.5 }}>
                Last month
              </ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <UsageTiles usage={usage} loading={usageLoading} />
        {usage?.available && <ChargesSummary usage={usage} />}
      </Box>

      {!overview && !loadError && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {overview && (
        <>
          {overview.pricing && (
            <Box component="section" sx={{ mb: 4 }}>
              <SectionTitle
                title="Prices"
                hint="What each SMS and WhatsApp message costs. You're only charged for messages that were delivered. When a price changes, messages already sent keep the price they were sent at."
              />
              <PriceList pricing={overview.pricing} />
            </Box>
          )}

          <Box component="section" sx={{ mb: 4 }}>
            <SectionTitle
              title="Messages"
              hint={
                isOwner
                  ? "Turn a switch off to stop that message on that channel. Changes apply right away. Faded switches are not available yet: point at one to see why."
                  : "Only the account owner can change these."
              }
            />
            <MessagesGrid events={overview.events} onToggle={isOwner ? toggle : undefined} saving={saving} />
          </Box>

          <Box component="section" sx={{ mb: 4 }}>
            <SectionTitle title="Channels" hint="App notifications and email are included. SMS and WhatsApp cost extra." />
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              }}
            >
              {overview.channels.map((info) => (
                <ChannelCard
                  key={info.channel}
                  info={info}
                  pricing={overview.pricing?.channels.find((p) => p.channel === info.channel)}
                  currency={overview.pricing?.currency ?? "USD"}
                  defaultSender={overview.defaultSenderName}
                  contactWhatsapp={overview.contactWhatsapp}
                  isOwner={isOwner}
                  requesting={requesting === info.channel}
                  onRequest={request}
                />
              ))}
            </Box>
            <Typography sx={{ fontSize: "0.88rem", color: "text.secondary", mt: 2 }}>
              Messages are sent as <strong>{overview.defaultSenderName}</strong> unless you have your own sender
              name. Want your own?{" "}
              {senderContact ? (
                <Link href={senderContact} target="_blank" rel="noopener noreferrer">
                  Contact us
                </Link>
              ) : (
                "Contact us"
              )}{" "}
              and we'll set it up.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default NotificationCenter;
